import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '../../../../../services/translate/translate.service';
import { ServerApiService } from '../../../../../server-api/server-api.service';
import { ReceiveUploadedFileRequest } from '../../../../../models/upload/receive-uploaded-file-request';
import { ReceiveUploadedFileResult } from '../../../../../models/upload/receive-uploaded-file-result';
import { LogService } from '../../../../../services/log/log.service';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { UploadFileContentType } from '../../../../../models/upload/upload-file-content-type';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '../../../../../models/User/user';
import { Step } from '../../../../../shared-components/custom-stepper/custom-stepper.component';
import { FileUploadComponent } from '../../../../../shared-components/file-upload/file-upload.component';
import { UploadSuppliersInstructionsModalComponent } from '../instructions-modal/upload-suppliers-instructions-modal.component';
import { AppContextService } from '../../../../../services/app-context.service';
import { RouteAuthorizationService } from '../../../../../services/authorization/route-authorization.service';
import { UtilitiesService } from '../../../../../services/utils/utilities.service';
import { CustomModalService } from '../../../../../shared-components/custom-modal/custom-modal.service';

@Component({
	selector: 'app-suppliers-upload-step2',
	templateUrl: './suppliers-upload-step2.component.html',
	styleUrls: ['./suppliers-upload-step2.component.css'],
	standalone: false,
})
export class SuppliersUploadStep2Component implements OnInit {
	@ViewChild('fileUpload') fileUpload!: FileUploadComponent;
	public fileName: string;
	private fileType: string;
	private fileContent: string;
	public isPerformingRequest = false;
	public loggedUser: User | null = null;
	private associatedCompanyId: string;
	private returnContractId: string = '';

	currentStep: number = 1;
	steps: Step[] = [];

	constructor(
		private translateService: TranslateService,
		private serverApiService: ServerApiService,
		private logger: LogService,
		private toasterService: ToasterService,
		private router: Router,
		private route: ActivatedRoute,
		private modalService: CustomModalService,
		private appContext: AppContextService,
		private routeAuthorizationService: RouteAuthorizationService,
		private utilitiesService: UtilitiesService
	) {
		this.initializeSteps();
	}

	private initializeSteps(): void {
		this.steps = [
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step1Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step2Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step3Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step4Label', 'client'),
				icon: 'fa-solid fa-check',
			},
		];
	}

	ngOnInit() {
		const queryParams = this.route.snapshot.queryParams;
		if (queryParams['returnContractId']) {
			this.returnContractId = queryParams['returnContractId'];
		}

		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (
				(this.loggedUser?.IsBuyer && this.loggedUser?.IsCompanyAdmin) ||
				(this.loggedUser?.IsSystemAdmin && !this.loggedUser.IsApiIntegrationUser) ||
				this.loggedUser?.IsSupportOperator
			) {
				this.route.params.subscribe((params) => {
					this.associatedCompanyId = params['associatedCompanyId'] || '';

					if (!this.associatedCompanyId) {
						this.logger.error('Missing params on suppliers upload step2.', this.associatedCompanyId);
						this.toasterService.showError(
							this.translateService.translate('suppliers-upload-step2.missing-params-on-suppliers-upload-step2', 'client')
						);
						return;
					}
				});
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});

		this.fileName = this.translateService.translate('supplier-upload-step2.SupplierUploadNoSelectedFile', 'client');
	}

	public processUploadedSupplier(): void {
		const receiveUploadedFileRequest: ReceiveUploadedFileRequest = <ReceiveUploadedFileRequest>{
			FileContent: this.fileContent,
			FileName: this.fileName,
			UploadFileContentType: UploadFileContentType.ConfirmingSuppliers_Type1_XLSX,
			FileType: this.fileType,
		};

		this.isPerformingRequest = true;
		this.serverApiService.receiveUploadedFile(receiveUploadedFileRequest).subscribe({
			next: (operationResult: OperationResult<ReceiveUploadedFileResult>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					const queryParams: any = {};
					if (this.returnContractId) {
						queryParams.returnContractId = this.returnContractId;
					}

					const currentQueryParams = this.route.snapshot.queryParams;
					if (currentQueryParams['buyerId']) {
						queryParams.buyerId = currentQueryParams['buyerId'];
					}
					this.router.navigate(['/private/files-upload/upload/suppliers/step3', this.associatedCompanyId, operationResult.Data.FileId], {
						queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
					});
				} else {
					const errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);
					this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
						errors: errors,
						autoClose: false,
					});
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[SuppliersUploadStep2Component::receiveUploadedFileRequest] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	protected onFileChange(event): void {
		let reader = new FileReader();
		if (event.target.files && event.target.files.length > 0) {
			let file = event.target.files[0];
			reader.readAsDataURL(file);
			reader.onload = () => {
				this.fileName = file.name;
				this.fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
			};
			reader.onloadend = () => {
				if (reader.result !== null && reader.result !== undefined && reader.result.toString() !== null) {
					var base64result = (reader.result as string).split(',')[1];
					this.fileContent = base64result;
					this.processUploadedSupplier();
				}
			};
		}
	}

	protected handleFilesChange(files: File[]): void {
		if (files.length === 0) {
			this.fileName = this.translateService.translate('supplier-upload-step2.SupplierUploadNoSelectedFile', 'client');
			this.fileContent = '';
			this.fileType = '';
		}
	}

	protected selectFile(): void {
		this.fileUpload.openFileDialog();
	}

	protected openInstructionsModal(): void {
		const instructionsModalRef = this.modalService.open<string>({
			size: 'lg',
			title: this.translateService.translate('supplier-upload-instructions-modal.title', 'client'),
			body: {
				kind: 'component',
				componentType: UploadSuppliersInstructionsModalComponent,
			},
			actions: [
				{
					id: 'start',
					label: this.translateService.translate('button.start', 'client') || 'Start',
					color: 'primary',
					variant: 'outlined',
					autoFocus: true,
					run: ({ bodyComponent, modalRef }) => {
						const result = bodyComponent?.start();
						modalRef.close(result);
					},
				},
			],
		});

		instructionsModalRef.result.catch(() => undefined);
	}

	protected redirectToDocumentsStep1(): void {
		const queryParams: any = {};
		if (this.returnContractId) {
			queryParams.returnContractId = this.returnContractId;
		}

		const currentQueryParams = this.route.snapshot.queryParams;
		if (currentQueryParams['buyerId']) {
			queryParams.buyerId = currentQueryParams['buyerId'];
		}
		this.router.navigate(['/private/files-upload/upload/suppliers/step1'], {
			queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
		});
	}
}
